import { useEffect, useState } from "react";
import { TProcessStates } from "../../../lib/types/general";
import { LabSignUpFormData, TApiLab, TLandmarks } from "../../../lib/types/labs";
import { useLabs } from "../../../services/laboratories";
import { Spinner } from "../../common/spinner";
import { DEVICES_SIZES_IN_PX } from "../../../lib/common/constants";
import { LifestyleOverlayCard } from "./lifestyle-overlay-card";

type TLandmarksProps = {
    editable: boolean;
    isSomePartBeingEdited: boolean;
    userInfo: TApiLab;
    workingState: TProcessStates;
    setUserInfo: React.Dispatch<React.SetStateAction<TApiLab>>;
    setIsSomePartBeingEdited: (value: React.SetStateAction<boolean>) => void;
    updateLabAndLocalStorage: (data: LabSignUpFormData, name?: string) => Promise<void>;
};

export const Landmarks = ({
    editable,
    isSomePartBeingEdited,
    userInfo,
    workingState,
    setUserInfo,
    setIsSomePartBeingEdited,
    updateLabAndLocalStorage,
}: TLandmarksProps) => {
    const [landmarksPerPage, setlandmarksPerPage] = useState(5);
    const totalLandmarks = userInfo.other.aboutLandmarks || [];
    const totalItems = Math.ceil(totalLandmarks.length / landmarksPerPage);
    const [visibleLandmarks, setVisibleLandmarks] = useState(5);

    const [isExpandedLandmarks, setIsExpandedLandmarks] = useState(false);
    const [currWorkingStateLandmarks, setCurrWorkingStateLandmarks] = useState<TProcessStates>("done");
    const [selectedLandmarks, setSelectedLandmarks] = useState<TLandmarks[]>([]);

    const fetchLandmarksData = async () => {
        const zip = userInfo.other?.zip || "";
        if (!zip || zip === "") {
            return;
        }
        setCurrWorkingStateLandmarks("working");
        try {
            const response = await getLandmarks(zip);
            const landmarks = response.data?.businesses || [];
            const userInfoSelectedLandmarksMap = userInfo.other?.aboutLandmarks
                ? userInfo.other.aboutLandmarks.reduce(
                      (prev, curr) => {
                          return {
                              ...prev,
                              [curr.id]: curr,
                          };
                      },
                      {} as Record<string, any>,
                  )
                : {};
            //we set the selected landmarks
            landmarks.forEach((r) => {
                r.isSelected = userInfoSelectedLandmarksMap[r.id] !== undefined ? true : false;
            });
            setSelectedLandmarks(landmarks);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setCurrWorkingStateLandmarks("idle");
        }
    };
    const { getLandmarks } = useLabs();
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= DEVICES_SIZES_IN_PX.medium) {
                setlandmarksPerPage(3);
            } else {
                setlandmarksPerPage(5);
            }
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const renderCarouselItems = () => {
        const carouselItems = [];

        for (let i = 0; i < totalItems; i++) {
            const startIdx = i * landmarksPerPage;
            const endIdx = startIdx + landmarksPerPage;
            const currentLandmarks = totalLandmarks.slice(startIdx, endIdx);

            const cardGroup = currentLandmarks.map((landmark) => (
                <div className="col" key={landmark.id}>
                    {/* non editable carrousel */}
                    <LifestyleOverlayCard
                        className="min-height-cards-lifestyle w-auto"
                        imageSrc={landmark.image_url}
                        imageAlt="landmark"
                        href={landmark.url}
                        title={landmark.name}
                        line1={
                            <>
                                <i className="fa-solid fa-star text-primary me-2"></i> {landmark.rating}
                            </>
                        }
                        line2={
                            <>
                                <i className="fa-solid fa-location-dot me-2"></i>
                                {landmark.location?.address1 || "Address"}
                            </>
                        }
                    />
                </div>
            ));

            //hack to make all the card equally width when there is less than what the page allows
            if (cardGroup.length < landmarksPerPage) {
                const remainingCards = landmarksPerPage - cardGroup.length;
                for (let j = 0; j < remainingCards; j++) {
                    const DummyEmptyCard = <div className="col" key={`landmark-empty-${i}-${j}`} aria-hidden="true"></div>;
                    cardGroup.push(DummyEmptyCard);
                }
            }
            carouselItems.push(
                <div className={`carousel-item ${i === 0 ? "active" : ""}`} key={i}>
                    <div className={"row g-3 row-cols-1 row-cols-md-" + landmarksPerPage}>{cardGroup}</div>
                </div>,
            );
        }

        return carouselItems;
    };

    const openLandmarkOptions = () => {
        setIsExpandedLandmarks(!isExpandedLandmarks);
        fetchLandmarksData();
        setIsSomePartBeingEdited(true);
    };
    const handleCheckboxChange = (e: any, landmark: TLandmarks) => {
        e.stopPropagation();
        setSelectedLandmarks((prev) => {
            const idx = prev.findIndex((r) => r.id === landmark.id);
            prev[idx].isSelected = !prev[idx].isSelected;
            return prev;
        });
    };

    const saveLandmarks = async (e: any) => {
        e.preventDefault();
        e.stopPropagation();

        setCurrWorkingStateLandmarks("working");
        const selectedInfo = selectedLandmarks.filter((r) => r.isSelected);
        const newLandmarks = {
            ...userInfo.other,
            aboutLandmarks: selectedInfo,
        };

        await updateLabAndLocalStorage(
            {
                password: "",
                ...newLandmarks,
            },
            "",
        );

        setUserInfo({
            ...userInfo,
            other: {
                ...userInfo.other,
                aboutLandmarks: selectedInfo,
            },
        });

        setIsExpandedLandmarks(false);
        setIsSomePartBeingEdited(false);
        setCurrWorkingStateLandmarks("done");
    };

    const cancelHandler = async () => {
        setIsExpandedLandmarks(false);
        setIsSomePartBeingEdited(false);
    };
    return (
        <>
            {!editable && userInfo.other.aboutLandmarks && (
                <div className="container mb-4 py-3 lifestyle-panel">
                    <div className="row">
                        <h6 className="mb-3 fw-bold text-primary">FAVORITE LANDMARKS</h6>
                        <div className="d-none d-md-block">
                            {/* non editable desktop carrousel - techs view*/}
                            <div className="min-height-cards-lifestyle w-100">
                                {workingState === "working" && <p className="p-3"> Looking for landmarks in this area...</p>}
                                {["idle", "done"].includes(workingState) && userInfo.other.aboutLandmarks && userInfo.other.aboutLandmarks.length > 0 && (
                                    <>
                                        <div id="landCarousel" className="carousel slide" data-bs-ride="carousel" data-bs-interval="false">
                                            <div className="carousel-inner">{renderCarouselItems()}</div>
                                            {userInfo.other.aboutLandmarks.length > 5 && (
                                                <div className="">
                                                    <a className="carousel-control-prev" href="#landCarousel" role="button" data-bs-slide="prev">
                                                        <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                                                        <span className="visually-hidden">Previous</span>
                                                    </a>
                                                    <a className="carousel-control-next" href="#landCarousel" role="button" data-bs-slide="next">
                                                        <span className="carousel-control-next-icon" aria-hidden="true"></span>
                                                        <span className="visually-hidden">Next</span>
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                                {["idle", "done"].includes(workingState) && userInfo.other.aboutLandmarks?.length === 0 && <p> No landmarks found.</p>}
                            </div>
                        </div>
                        {/* non editable mobile - this is not a carrousel, its a stack of cards - techs view*/}
                        <div className="d-block d-md-none">
                            {workingState === "working" && <p> Looking for landmarks in this area...</p>}
                            {["idle", "done"].includes(workingState) && userInfo.other.aboutLandmarks && userInfo.other.aboutLandmarks.length > 0 && (
                                <>
                                    <div>
                                        {userInfo.other.aboutLandmarks.slice(0, visibleLandmarks).map((landmark) => (
                                            <div key={landmark.id} className="my-2">
                                                <LifestyleOverlayCard
                                                    className="mb-3"
                                                    imageSrc={landmark.image_url}
                                                    imageAlt="landmark"
                                                    href={landmark.url}
                                                    title={landmark.name}
                                                    line1={
                                                        <>
                                                            <i className="fa-solid fa-star text-primary"></i> {landmark.rating}
                                                        </>
                                                    }
                                                    line2={
                                                        <>
                                                            <i className="fa-solid fa-location-dot"></i>
                                                            {landmark.location?.address1 || "Address"}
                                                        </>
                                                    }
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    {visibleLandmarks < userInfo.other.aboutLandmarks.length && (
                                        <div className="text-center">
                                            <button className="btn btn-link fw-semibold" onClick={() => setVisibleLandmarks((prev) => prev + 5)}>
                                                View More
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                            {["idle", "done"].includes(workingState) && userInfo.other.aboutLandmarks?.length === 0 && <p> No landmarks found.</p>}
                        </div>
                    </div>
                </div>
            )}
            {editable && (
                <div className="container mb-4 py-3 lifestyle-panel">
                    <div className="row">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0 fw-bold text-primary">FAVORITE LANDMARKS</h6>
                            {!isSomePartBeingEdited && (
                                <div
                                    className="fileTooltip rightEdit"
                                    data-tooltip=" Edit "
                                    role="button"
                                    aria-expanded="false"
                                    aria-controls="collapseRestaurant"
                                    onClick={openLandmarkOptions}
                                >
                                    <i data-bs-testid="edit-icon" className="fa-solid fa-pen-to-square fa-xs p-2 text-center shadow editIcon"></i>
                                </div>
                            )}
                        </div>
                        {isExpandedLandmarks && editable && (
                            <div>
                                <div className="card card-body mb-3">
                                    <p>Select the landmarks you would like to display.</p>
                                    <div>
                                        {currWorkingStateLandmarks === "working" && (
                                            <div className="text-center">
                                                <Spinner spinnerColor="text-primary" />
                                            </div>
                                        )}
                                        {currWorkingStateLandmarks === "idle" && selectedLandmarks.length > 0 && (
                                            <div className="row">
                                                {selectedLandmarks.map((landmark) => (
                                                    <div className="col-md-6 col-lg-4 mb-3" key={landmark.id}>
                                                        <div className="input-group h-100">
                                                            <label className="w-100" htmlFor={`rChk_${landmark.id}`}>
                                                                <div className="row align-items-center input-group-text w-100 h-100 p-2">
                                                                    <div className="col-2 p-0">
                                                                        <input
                                                                            type="checkbox"
                                                                            defaultChecked={landmark.isSelected}
                                                                            id={`rChk_${landmark.id}`}
                                                                            className="form-check-input"
                                                                            onChange={(e) => handleCheckboxChange(e, landmark)}
                                                                        />
                                                                    </div>
                                                                    <div className="col-4 p-0">
                                                                        <img src={landmark.image_url} className="rounded label-img" alt="landmark" />
                                                                    </div>
                                                                    <div className="col-6 text-wrap">
                                                                        <p
                                                                            style={{
                                                                                fontSize: "smaller",
                                                                            }}
                                                                        >
                                                                            <strong>{landmark.name}</strong>
                                                                            <br />
                                                                            <small>
                                                                                <i className="fa-solid fa-star fa-xs"></i> {landmark.rating}
                                                                            </small>
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </label>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {currWorkingStateLandmarks === "idle" && selectedLandmarks.length === 0 && <p>No landmarks found in this area.</p>}
                                    </div>
                                    <div className="d-grid gap-3 d-md-block">
                                        <button className="btn btn-primary" onClick={saveLandmarks}>
                                            Save
                                        </button>
                                        <button className="btn btn-danger ms-md-3" onClick={cancelHandler}>
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* editable desktop carrousel - labs view*/}
                        <div className="d-none d-md-block">
                            <div className="min-height-cards-lifestyle w-100">
                                {workingState === "working" && <p className="p-3"> Looking for landmarks in this area...</p>}
                                {["idle", "done"].includes(workingState) && userInfo.other.aboutLandmarks && userInfo.other.aboutLandmarks.length > 0 && (
                                    <>
                                        <div id="landCarousel" className="carousel slide" data-bs-ride="carousel" data-bs-interval="false">
                                            <div className="carousel-inner">{renderCarouselItems()}</div>
                                            {userInfo.other.aboutLandmarks.length > 5 && (
                                                <div className="">
                                                    <a className="carousel-control-prev" href="#landCarousel" role="button" data-bs-slide="prev">
                                                        <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                                                        <span className="visually-hidden">Previous</span>
                                                    </a>
                                                    <a className="carousel-control-next" href="#landCarousel" role="button" data-bs-slide="next">
                                                        <span className="carousel-control-next-icon" aria-hidden="true"></span>
                                                        <span className="visually-hidden">Next</span>
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                                {["idle", "done"].includes(workingState) && userInfo.other.aboutLandmarks?.length === 0 && <p> No landmarks found.</p>}
                            </div>
                        </div>
                        {/* editable mobile - this is not a carrousel, its a stack of cards - techs view*/}
                        <div className="d-block d-md-none">
                            {workingState === "working" && <p> Looking for landmarks in this area...</p>}
                            {["idle", "done"].includes(workingState) && userInfo.other.aboutLandmarks && userInfo.other.aboutLandmarks.length > 0 && (
                                <>
                                    <div>
                                        {userInfo.other.aboutLandmarks.slice(0, visibleLandmarks).map((landmark) => (
                                            <div className="my-2" key={landmark.id}>
                                                <LifestyleOverlayCard
                                                    className="mb-3"
                                                    imageSrc={landmark.image_url}
                                                    imageAlt="landmark"
                                                    href={landmark.url}
                                                    title={landmark.name}
                                                    line1={
                                                        <>
                                                            <i className="fa-solid fa-star text-primary me-2"></i> {landmark.rating}
                                                        </>
                                                    }
                                                    line2={
                                                        <>
                                                            <i className="fa-solid fa-location-dot me-2"></i>
                                                            {landmark.location?.address1 || "Address"}
                                                        </>
                                                    }
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    {visibleLandmarks < userInfo.other.aboutLandmarks.length && (
                                        <div className="text-center">
                                            <button className="btn btn-link fw-semibold" onClick={() => setVisibleLandmarks((prev) => prev + 5)}>
                                                View More
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                            {["idle", "done"].includes(workingState) && userInfo.other.aboutLandmarks?.length === 0 && <p> No landmarks found.</p>}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
